import { useState, type ReactNode } from 'react';
import { StaffAttendanceMonth } from './StaffAttendanceMonth';
import { StaffLogModal, type StaffLogTarget } from './StaffLogModal';
import { useClientPaged } from '../hooks/useClientPaged';
import { TablePagination } from './TablePagination';
import { Badge, Button, Group, SegmentedControl, Card, Stack, useMantineTheme, Table, Text, Loader, Center, Alert, TextInput, ActionIcon, Tooltip } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconCheck, IconLogout, IconUserCheck, IconInfoCircle, IconSearch, IconX, IconArrowBackUp, IconClockEdit, IconPencil } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mantine/hooks';
import api from '../services/api';
import { SectionCard } from './SectionCard';
import { Specialty } from './Specialty';
import { FetchOverlay } from './FetchOverlay';
import { uzDateProps } from '../lib/dateInput';

interface Javob {
  ishKuni: boolean;
  sana: string;
  qatorlar: Qator[];
}

interface Qator {
  user: { id: string; fullName: string; role: string; specialty?: string | null };
  log: { id: string; checkIn: string; checkOut?: string | null; note?: string | null } | null;
}

/** Sana → `YYYY-MM-DD` (so'rov parametri uchun; brauzer mahalliy vaqti = Toshkent). */
const kalit = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const soat = (v?: string | null) =>
  v ? new Date(v).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '—';

/**
 * Xodimlar davomati — QO'LDA.
 *
 * Asosiy yo'l yuz tanish terminali: u ishlaganda yozuvlar o'zidan tushadi va bu
 * sahifa faqat ko'rish uchun bo'ladi. Terminal nosoz bo'lgan yoki hali
 * sozlanmagan kunlarda esa admin shu yerdan belgilaydi.
 *
 * Ikkala manba BITTA `WorkLog` jadvaliga yozadi, shuning uchun ular to'qnashmaydi:
 * terminal kunning eng erta vaqtini `checkIn`, eng kechini `checkOut` qilib
 * qo'yadi; bu yerdan bosilgan tugma ham xuddi o'sha yozuvni yangilaydi.
 */
export function StaffAttendance() {
  const { t } = useTranslation();
  const [rejim, setRejim] = useState<'day' | 'month'>('day');
  const tugma = (
    <SegmentedControl
      size="xs"
      value={rejim}
      onChange={(v) => setRejim(v as 'day' | 'month')}
      data={[
        { value: 'day', label: t('staffAttendance.modeDay') },
        { value: 'month', label: t('staffAttendance.modeMonth') },
      ]}
    />
  );
  if (rejim === 'month') {
    return (
      <SectionCard title={t('staffAttendance.title')} icon={IconUserCheck} p={0} right={tugma}>
        <StaffAttendanceMonth />
      </SectionCard>
    );
  }
  return <KunlikDavomat rejimTugma={tugma} />;
}

/** Bitta kun — qo'lda "keldi / ketdi" belgilash. */
function KunlikDavomat({ rejimTugma }: { rejimTugma: ReactNode }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [sana, setSana] = useState<Date | null>(new Date());

  const kun = sana ?? new Date();
  const { data: javob, isLoading, isFetching } = useQuery({
    queryKey: ['staff-attendance', kalit(kun)],
    queryFn: async () =>
      (await api.get<Javob>('/payroll/logs/day', { params: { date: kalit(kun) } })).data,
  });
  const qatorlar = javob?.qatorlar ?? [];
  /* Dam olish kunimi — SERVER aytadi. Brauzerda hafta kunini sanash yetarli
     emas: maktab shanba ishlashi ham, ishlamasligi ham mumkin, ustiga bayram
     kunlari bor. Yagona manba serverda (`lib/workdays.ts`). */
  const damOlish = javob ? !javob.ishKuni : false;

  /* QIDIRUV — xodim ismi yoki roli bo'yicha.
     50+ xodim orasidan kerakligini topish uchun sahifalarni varaqlash kerak
     edi; qidiruv esa butun RO'YXAT bo'yicha ishlaydi, faqat ko'rinib turgan
     sahifa bo'yicha emas — aks holda u foydasiz bo'lardi. */
  const [qidiruv, setQidiruv] = useState('');
  const izla = qidiruv.trim().toLowerCase();
  const topilgan = izla
    ? qatorlar.filter(
        (x) =>
          x.user.fullName.toLowerCase().includes(izla) ||
          t(`roles.${x.user.role}`).toLowerCase().includes(izla)
      )
    : qatorlar;

  /* Ro'yxat to'liq olinadi (serverda sahifalash yo'q) — ko'rsatishni
     brauzerda bo'lamiz: uzun jadval sahifani sekinlashtiradi. */
  const sahifa = useClientPaged(topilgan, 'staff-attendance');

  const yangila = () => {
    qc.invalidateQueries({ queryKey: ['staff-attendance', kalit(kun)] });
    // Kelish-ketish maoshga ta'sir qiladi (ish vaqti qoidasi) — xodim kartasi, Xodimlar ro'yxati va Maosh sahifasi
    for (const key of ['salary-compute', 'payroll-summary', 'payroll-oylik']) qc.invalidateQueries({ queryKey: [key] });
  };
  const xato = (e: unknown) =>
    notifications.show({
      color: 'red',
      message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
    });

  const keldi = useMutation({
    mutationFn: (userId: string) => api.post('/payroll/logs/check-in', { userId, note: t('staffAttendance.manual') }),
    onSuccess: yangila,
    onError: xato,
  });
  const ketdi = useMutation({
    mutationFn: (logId: string) => api.post(`/payroll/logs/${logId}/check-out`, {}),
    onSuccess: yangila,
    onError: xato,
  });

  /* BEKOR QILISH — "ketdi" ni adashib bosish juda oson: u kelish tugmasi
     turgan joyda paydo bo'ladi, ya'ni xuddi shu nuqtaga ikkinchi marta
     bosilsa bo'ldi. Ilgari buni qaytarish uchun bazaga kirish kerak edi.
     Kelish vaqti TEGILMAYDI — faqat ketish tozalanadi. */
  const bekor = useMutation({
    mutationFn: (logId: string) => api.patch(`/payroll/logs/${logId}`, { checkOut: null }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('staffAttendance.undone') });
      yangila();
    },
    onError: xato,
  });

  /* Kelajak kuni uchun davomat belgilash mantiqsiz — tugmalar yashiriladi.
     Serverda ham cheklov bor, lekin tugmani ko'rsatib turib rad etish yomon UX. */
  const bugun = kalit(new Date());
  /* Dam olish kunida tugmalar UMUMAN ko'rsatilmaydi: server baribir rad etadi,
     tugmani ko'rsatib turib "bo'lmaydi" deyish esa yomon UX. */
  const otganYokiBugun = kalit(kun) <= bugun && !damOlish;
  /* Tezkor tugmalar HOZIRGI vaqtni yozadi — bu faqat BUGUN uchun to'g'ri.
     O'tgan kunda ular o'rniga vaqt so'raydigan oyna ochiladi: aks holda
     "3-sentabr" tanlab "Keldi" bosilsa, yozuv bugungi soat bilan tushardi. */
  const bugunmi = kalit(kun) === bugun;
  const [tahrir, setTahrir] = useState<StaffLogTarget | null>(null);
  const och = (q: Qator) => setTahrir({ user: q.user, sana: kalit(kun), log: q.log });

  const kelganlar = qatorlar.filter((q) => q.log).length;
  /* TELEFONDA jadval o'rniga kartochkalar: 5 ustunli jadval 390px ekranga
     sig'mas edi — yonga surib, kerakli tugmani izlash kerak bo'lardi. */
  const theme = useMantineTheme();
  const telefon = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  /** Qator amallari — jadval (kompyuter) va kartochka (telefon) uchun BIR XIL. */
  const amallar = (q: Qator) =>
    !otganYokiBugun ? null : (
      <Group gap={6} wrap="nowrap">
        {!q.log ? (
          bugunmi ? (
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconCheck size={14} />}
              loading={keldi.isPending && keldi.variables === q.user.id}
              onClick={() => keldi.mutate(q.user.id)}
            >
              {t('staffAttendance.markIn')}
            </Button>
          ) : (
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconClockEdit size={14} />}
              onClick={() => och(q)}
            >
              {t('staffAttendance.markWithTime')}
            </Button>
          )
        ) : !q.log.checkOut ? (
          <Button
            size="compact-sm"
            variant="subtle"
            color="orange"
            leftSection={<IconLogout size={14} />}
            loading={ketdi.isPending && ketdi.variables === q.log.id}
            onClick={() => (bugunmi ? ketdi.mutate(q.log!.id) : och(q))}
          >
            {t('staffAttendance.markOut')}
          </Button>
        ) : (
          <>
            <Badge color="teal" variant="light">{t('staffAttendance.done')}</Badge>
            <Tooltip label={t('staffAttendance.undoOut')} withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label={t('staffAttendance.undoOut')}
                loading={bekor.isPending && bekor.variables === q.log!.id}
                onClick={() => bekor.mutate(q.log!.id)}
              >
                <IconArrowBackUp size={15} />
              </ActionIcon>
            </Tooltip>
          </>
        )}
        {/* Vaqtni qo'lda kiritish / tuzatish — har qanday holatda */}
        {(q.log || bugunmi) && (
          <Tooltip label={q.log ? t('staffAttendance.edit') : t('staffAttendance.markWithTime')} withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label={q.log ? t('staffAttendance.edit') : t('staffAttendance.markWithTime')}
              onClick={() => och(q)}
            >
              {q.log ? <IconPencil size={15} /> : <IconClockEdit size={15} />}
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    );

  return (
    <SectionCard title={t('staffAttendance.title')} icon={IconUserCheck} p={0} pos="relative" right={rejimTugma}>
      <FetchOverlay visible={isFetching && !isLoading} />

      <Group p="md" pb="xs" justify="space-between" align="flex-end">
        <DateInput
          label={t('staffAttendance.date')}
          value={sana}
          onChange={setSana}
          {...uzDateProps}
          clearable={false}
          w={{ base: '100%', sm: 180 }}
        />
        <TextInput
          label={t('common.search')}
          placeholder={t('staffAttendance.searchPlaceholder')}
          value={qidiruv}
          onChange={(e) => setQidiruv(e.currentTarget.value)}
          leftSection={<IconSearch size={15} />}
          rightSection={
            qidiruv ? (
              <IconX
                size={15}
                style={{ cursor: 'pointer' }}
                onClick={() => setQidiruv('')}
              />
            ) : null
          }
          w={{ base: '100%', sm: 240 }}
        />
        <Text size="sm" c="dimmed">
          {/* Qidiruv yoqilganda TOPILGANI ham ko'rsatiladi — aks holda "12 dan 3 tasi
              keldi" degan yozuv butun maktabga tegishlidek tuyulardi. */}
          {izla
            ? t('staffAttendance.found', { topilgan: topilgan.length, jami: qatorlar.length })
            : t('staffAttendance.summary', { kelgan: kelganlar, jami: qatorlar.length })}
        </Text>
      </Group>

      <Alert
        variant="light"
        color={damOlish ? 'gray' : 'blue'}
        icon={<IconInfoCircle size={16} />}
        m="md"
        mt={0}
      >
        {damOlish ? t('staffAttendance.restDay') : t('staffAttendance.faceNote')}
      </Alert>

      {isLoading ? (
        <Center h={180}><Loader /></Center>
      ) : qatorlar.length === 0 ? (
        <Text size="sm" c="dimmed" p="md">{t('staffAttendance.empty')}</Text>
      ) : topilgan.length === 0 ? (
        <Text size="sm" c="dimmed" p="md">{t('common.noMatch')}</Text>
      ) : (
        <>
        {telefon ? (
          <Stack gap="xs" px="md" pb="md">
            {sahifa.items.map((q) => (
              <Card key={q.user.id} withBorder padding="sm" radius="md">
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                  <div style={{ minWidth: 0 }}>
                    <Text fw={600} size="sm">{q.user.fullName}</Text>
                    <Badge variant="light" size="xs" mt={4}>{t(`roles.${q.user.role}`)}</Badge>
                  </div>
                  <Text ff="monospace" size="sm" style={{ whiteSpace: 'nowrap' }}>
                    {soat(q.log?.checkIn)} – {soat(q.log?.checkOut)}
                  </Text>
                </Group>
                {otganYokiBugun && <Group mt="xs" justify="flex-end">{amallar(q)}</Group>}
              </Card>
            ))}
          </Stack>
        ) : (
        <Table.ScrollContainer minWidth={640}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('staffAttendance.staff')}</Table.Th>
                <Table.Th>{t('staffAttendance.role')}</Table.Th>
                <Table.Th>{t('staffAttendance.in')}</Table.Th>
                <Table.Th>{t('staffAttendance.out')}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sahifa.items.map((q) => (
                <Table.Tr key={q.user.id}>
                  <Table.Td fw={500}>{q.user.fullName}<Specialty value={q.user.specialty} /></Table.Td>
                  <Table.Td>
                    <Badge variant="light" size="sm">{t(`roles.${q.user.role}`)}</Badge>
                  </Table.Td>
                  <Table.Td ff="monospace">{soat(q.log?.checkIn)}</Table.Td>
                  <Table.Td ff="monospace">{soat(q.log?.checkOut)}</Table.Td>
                  <Table.Td>
                    {amallar(q)}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        )}
        <TablePagination
          page={sahifa.page}
          pages={sahifa.pages}
          total={sahifa.total}
          limit={sahifa.limit}
          onPage={sahifa.setPage}
          onLimit={sahifa.setLimit}
        />
        </>
      )}
      <StaffLogModal
        target={tahrir}
        onClose={() => setTahrir(null)}
        onSaved={() => {
          yangila();
          qc.invalidateQueries({ queryKey: ['staff-attendance-month'] });
        }}
      />
    </SectionCard>
  );
}
